import { useEffect, useRef, useState } from "react";
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import { process } from '@progress/kendo-data-query';
import { PrivateAxios } from "../../environment/AxiosInstance";
import { CurrencyList } from '../../utils/StaticData';

import * as XLSX from 'xlsx';
import moment from "moment";
import '@progress/kendo-theme-default/dist/all.css';
import './SalesTargets.css';
import { UserAuth } from '../auth/Auth';
import { Chart } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

//
// Sales Report. After the per-assignee refactor:
//   - Summary cards: sum of per-assignee assigned amounts / achievements.
//   - Grid: one row per (target, assignee) with Assignee column +
//           Last Achievement Date column.
//   - Chart: stacked bars per target with one segment per assignee.
//   - Period Roll-up still rolls up by chosen period; totals derive from
//     per-assignee sums.
//
const FILTER_DEBOUNCE_MS = 300;

export default function SalesReport() {
  const { MatchPermission } = UserAuth();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [emptyMessage, setEmptyMessage] = useState(null);
  const [summary, setSummary] = useState({});
  const [performanceData, setPerformanceData] = useState([]); // one row per (target, assignee)
  const [assigneesList, setAssigneesList] = useState([]);
  const [targetType, setTargetType] = useState('amount');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [dataState, setDataState] = useState({ skip: 0, take: 25, sort: [] });
  const [periodReportData, setPeriodReportData] = useState([]);
  const [reportPeriod, setReportPeriod] = useState('monthly');
  const [showPeriodReport, setShowPeriodReport] = useState(false);
  const [baseCurrency, setBaseCurrency] = useState('INR');
  // Last Achievement Date filter (client-side on performanceData)
  const [lastAchFrom, setLastAchFrom] = useState('');
  const [lastAchTo, setLastAchTo] = useState('');

  const debounceTimerRef = useRef(null);
  const abortControllerRef = useRef(null);

  // MatchPermission is in deps so this effect re-fires once permissions
  // load on hard refresh. See Auth.js — MatchPermission is stable via
  // useCallback, so its identity flips exactly once (permissions [] -> [...])
  // and the fetch fires exactly once. Without this, hard refresh leaves the
  // page blank because the first run sees permissions=[] and skips fetch.
  useEffect(() => {
    if (MatchPermission(['Sales All', 'View Sales', 'Edit Sales', 'Add Sales', 'Doer Edit Sales'])) {
      fetchAssignees();
    }
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [MatchPermission]);

  useEffect(() => {
    if (!MatchPermission(['Sales All', 'View Sales', 'Edit Sales', 'Add Sales', 'Doer Edit Sales'])) return;
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      if (showPeriodReport) fetchPeriodReport();
      else fetchDashboardData();
    }, FILTER_DEBOUNCE_MS);
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetType, fromDate, toDate, selectedAssignee, showPeriodReport, reportPeriod, MatchPermission]);

  if (!MatchPermission(['Sales All', 'View Sales', 'Edit Sales', 'Add Sales', 'Doer Edit Sales'])) {
    return (
      <div className="p-4 text-center">
        <h4>Access Denied</h4>
        <p>You don't have permission to view sales dashboard.</p>
      </div>
    );
  }

  function newAbortSignal() {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    return abortControllerRef.current.signal;
  }

  const fetchAssignees = async () => {
    try {
      const res = await PrivateAxios.get('/sales/reports/assignees');
      if (res.data && res.data.success) {
        const users = res.data.data || [];
        const uniqueUsers = Array.from(new Map(users.map(u => [u.id, u])).values());
        setAssigneesList(uniqueUsers);
      }
    } catch (err) { /* non-fatal */ }
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);
    setEmptyMessage(null);
    const signal = newAbortSignal();
    try {
      const params = new URLSearchParams({
        targetType,
        ...(fromDate && { fromDate }),
        ...(toDate && { toDate }),
        ...(selectedAssignee && { assigneeId: selectedAssignee })
      });
      const [summaryRes, performanceRes] = await Promise.all([
        PrivateAxios.get(`/sales/reports/summary?${params}`, { signal }),
        PrivateAxios.get(`/sales/reports/performance?${params}`, { signal })
      ]);
      if (summaryRes.data && summaryRes.data.success) {
        setSummary(summaryRes.data.data || {});
        if (summaryRes.data.data && summaryRes.data.data.baseCurrency) setBaseCurrency(summaryRes.data.data.baseCurrency);
      } else throw new Error('Failed to fetch summary data');
      if (performanceRes.data && performanceRes.data.success) {
        const rows = performanceRes.data.data || [];
        setPerformanceData(rows);
        if (performanceRes.data.baseCurrency) setBaseCurrency(performanceRes.data.baseCurrency);
        if (rows.length === 0) {
          setEmptyMessage(selectedAssignee
            ? 'No targets found for the selected assignee in this range.'
            : 'No targets match the current filters.');
        }
      } else throw new Error('Failed to fetch performance data');
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED' || err.message === 'canceled') return;
      console.error('Fetch dashboard data error:', err);
      setError((err && err.message) || 'Failed to load dashboard data. Please try again.');
      setSummary({});
      setPerformanceData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPeriodReport = async () => {
    setLoading(true);
    setError(null);
    setEmptyMessage(null);
    const signal = newAbortSignal();
    try {
      const params = new URLSearchParams({
        targetType, reportPeriod,
        ...(fromDate && { fromDate }),
        ...(toDate && { toDate }),
        ...(selectedAssignee && { assigneeId: selectedAssignee })
      });
      const res = await PrivateAxios.get(`/sales/reports/period-report?${params}`, { signal });
      if (res.data && res.data.success) {
        const rows = res.data.data || [];
        setPeriodReportData(rows);
        if (res.data.baseCurrency) setBaseCurrency(res.data.baseCurrency);
        if (rows.length === 0) {
          setEmptyMessage(selectedAssignee
            ? 'No targets found for the selected assignee in this range.'
            : 'No targets match the current filters.');
        }
      } else throw new Error('Failed to fetch period report data');
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED' || err.message === 'canceled') return;
      console.error('Fetch period report error:', err);
      setError((err && err.message) || 'Failed to load period report. Please try again.');
      setPeriodReportData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDataStateChange = (event) => setDataState(event.dataState);

  // Client-side filter: lastAchievementDate within [lastAchFrom, lastAchTo].
  // Rows without a last achievement date are excluded if any filter is set.
  const filteredPerformanceData = (() => {
    if (!lastAchFrom && !lastAchTo) return performanceData;
    return performanceData.filter((row) => {
      if (!row.lastAchievementDate) return false;
      const d = row.lastAchievementDate.slice(0, 10);
      if (lastAchFrom && d < lastAchFrom) return false;
      if (lastAchTo && d > lastAchTo) return false;
      return true;
    });
  })();

  const currencySymbol = (CurrencyList.find(c => c.code === baseCurrency) || {}).symbol || '';
  const formatValue = (value) => {
    if (targetType === 'amount') {
      const formatted = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value || 0);
      return `${currencySymbol}${formatted} ${baseCurrency}`;
    }
    return (value || 0).toString();
  };

  function buildFilterMetadata(kind) {
    const assigneeName = selectedAssignee
      ? ((assigneesList.find(a => String(a.id) === String(selectedAssignee)) || {}).name || selectedAssignee)
      : 'All Assignees';
    return [
      ['Sales Report Export'],
      ['Report Type', kind === 'period' ? `Period Roll-up (${reportPeriod})` : 'Target Summary'],
      ['Target Type', targetType],
      ['Base Currency', baseCurrency],
      ['Target Window From', fromDate || 'All'],
      ['Target Window To', toDate || 'All'],
      ['Assignee', assigneeName],
      ['Generated At', moment().format('YYYY-MM-DD HH:mm:ss')],
      [],
    ];
  }

  const exportToExcel = () => {
    let metadata = [];
    let dataRows = [];
    let sheetName = 'Sheet1';
    let fileName = 'Sales_Report.xlsx';
    const ts = moment().format('YYYY-MM-DD_HHmmss');

    if (showPeriodReport && periodReportData.length > 0) {
      // Multi-sheet export: Summary, Targets-per-period, Assignees-per-target,
      // and Daily achievements detail. Build the workbook directly here and
      // bypass the single-sheet flow that follows.
      const wb = XLSX.utils.book_new();
      const meta = buildFilterMetadata('period');

      // Sheet 1: Period summary
      const summaryHeaders = [
        'Period', '# Targets', 'Target Names',
        `Total Target (${baseCurrency})`,
        `Total Achievement (${baseCurrency})`,
        'Achievement %',
        `Remaining (${baseCurrency})`,
      ];
      const summaryRows = periodReportData.map(item => [
        item.period,
        item.targetCount,
        item.targetTitles || (item.targets || []).map(t => t.title).join(', '),
        item.totalTarget,
        item.totalAchievement,
        item.achievementPercentage,
        item.remaining,
      ]);
      const wsSummary = XLSX.utils.aoa_to_sheet([...meta, summaryHeaders, ...summaryRows]);
      XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');

      // Sheet 2: Per-target inside each period
      const targetHeaders = [
        'Period', 'Target Name', 'Currency (native)',
        `Target (${baseCurrency})`,
        `Achievement (${baseCurrency})`,
        '# Assignees',
        'Assignee Names',
      ];
      const targetRows = [];
      periodReportData.forEach(item => {
        (item.targets || []).forEach(t => {
          targetRows.push([
            item.period,
            t.title,
            t.currency || '',
            t.targetValue,
            t.achievement,
            (t.assignees || []).length,
            (t.assignees || []).map(a => a.name).join(', '),
          ]);
        });
      });
      const wsTargets = XLSX.utils.aoa_to_sheet([...meta, targetHeaders, ...targetRows]);
      XLSX.utils.book_append_sheet(wb, wsTargets, 'Targets per Period');

      // Sheet 3: ONE row per unique assignee — never duplicated across
      // (period, target) combinations. Totals are summed across every
      // period bucket and every target the assignee appears in:
      //   - Assigned / Achieved / Pending in the company's base currency
      //     (native columns are dropped because an assignee may have
      //     targets in multiple native currencies, which can't be summed)
      //   - Performance % = achieved / assigned
      //   - Last Achievement Date = max date across all targets
      //   - # Targets / # Periods / # Daily Entries = counts
      //   - Targets / Currencies columns list the distinct names/codes
      // The Daily Detail sheet (sheet 4) still has the full per-day audit
      // trail.
      const assigneeHeaders = [
        'Assignee',
        'Email',
        '# Targets',
        '# Periods',
        'Targets',
        'Currencies',
        `Assigned (${baseCurrency})`,
        `Achieved (${baseCurrency})`,
        `Pending (${baseCurrency})`,
        'Performance %',
        'Last Achievement Date',
        '# Daily Entries',
      ];
      const assigneeMap = new Map();
      periodReportData.forEach(item => {
        (item.targets || []).forEach(t => {
          (t.assignees || []).forEach(a => {
            // Backend sends assignee_id as `a.id`. Fall back to name to
            // avoid silently merging unrelated assignees if id is missing.
            const key = a.id != null ? `id:${a.id}` : `name:${a.name || ''}`;
            if (!assigneeMap.has(key)) {
              assigneeMap.set(key, {
                name: a.name || '',
                email: a.email || '',
                targetIds: new Set(),
                targetNames: new Set(),
                periods: new Set(),
                currencies: new Set(),
                assigned: 0,
                achieved: 0,
                lastDate: null,
                dailyCount: 0,
              });
            }
            const agg = assigneeMap.get(key);
            if (t.id != null) agg.targetIds.add(t.id);
            if (t.title) agg.targetNames.add(t.title);
            if (item.period) agg.periods.add(item.period);
            if (t.currency) agg.currencies.add(t.currency);
            agg.assigned += Number(a.assigned) || 0;
            agg.achieved += Number(a.achievement) || 0;
            if (a.lastAchievementDate) {
              if (!agg.lastDate || String(a.lastAchievementDate) > String(agg.lastDate)) {
                agg.lastDate = a.lastAchievementDate;
              }
            }
            agg.dailyCount += (a.daily || []).length;
          });
        });
      });
      const assigneeRows = Array.from(assigneeMap.values()).map(agg => {
        const pending = agg.assigned - agg.achieved;
        const perf = agg.assigned > 0
          ? Number(((agg.achieved / agg.assigned) * 100).toFixed(2))
          : 0;
        return [
          agg.name,
          agg.email,
          agg.targetIds.size || agg.targetNames.size,
          agg.periods.size,
          Array.from(agg.targetNames).join(', '),
          Array.from(agg.currencies).join(', '),
          Math.round(agg.assigned * 100) / 100,
          Math.round(agg.achieved * 100) / 100,
          Math.round(pending * 100) / 100,
          perf,
          agg.lastDate ? moment(agg.lastDate).format('DD-MM-YYYY') : '',
          agg.dailyCount,
        ];
      });
      const wsAssignees = XLSX.utils.aoa_to_sheet([...meta, assigneeHeaders, ...assigneeRows]);
      XLSX.utils.book_append_sheet(wb, wsAssignees, 'Assignee Breakdown');

      // Sheet 4: Assignee Drilldown — hierarchical layout. For each unique
      // assignee, ONE summary row (totals across everything) followed by
      // indented breakdown rows showing each (period, target) combination
      // they appear in. Lets you scan totals and drill into per-target
      // detail without having to filter or sort the flat Assignee Breakdown
      // sheet.
      const drilldownHeaders = [
        'Assignee / Detail',
        'Email',
        'Period',
        'Target',
        'Window',
        'Currency (native)',
        'Assigned (native)',
        'Achieved (native)',
        `Assigned (${baseCurrency})`,
        `Achieved (${baseCurrency})`,
        `Pending (${baseCurrency})`,
        'Performance %',
        'Last Achievement Date',
        '# Daily Entries',
      ];
      // First group raw (period, target, assignee) rows by assignee id so
      // each assignee's children land contiguously below their summary row.
      const drillGroups = new Map();
      periodReportData.forEach(item => {
        (item.targets || []).forEach(t => {
          const win = (t.startDate && t.endDate)
            ? `${moment(t.startDate).format('DD-MM-YYYY')} -> ${moment(t.endDate).format('DD-MM-YYYY')}`
            : '';
          (t.assignees || []).forEach(a => {
            const key = a.id != null ? `id:${a.id}` : `name:${a.name || ''}`;
            if (!drillGroups.has(key)) {
              drillGroups.set(key, {
                name: a.name || '',
                email: a.email || '',
                children: [],
              });
            }
            drillGroups.get(key).children.push({
              period: item.period,
              targetTitle: t.title,
              window: win,
              currency: t.currency || '',
              assignedNative: Number(a.assignedNative) || 0,
              achievedNative: Number(a.achievedNative) || 0,
              assigned: Number(a.assigned) || 0,
              achieved: Number(a.achievement) || 0,
              lastDate: a.lastAchievementDate || null,
              dailyCount: (a.daily || []).length,
            });
          });
        });
      });
      const drilldownRows = [];
      drillGroups.forEach(group => {
        // Summary row: sum across all of this assignee's (period, target)
        // entries. Native is blank because targets may span currencies.
        const totalAssigned = group.children.reduce((s, c) => s + c.assigned, 0);
        const totalAchieved = group.children.reduce((s, c) => s + c.achieved, 0);
        const totalPending = totalAssigned - totalAchieved;
        const totalPerf = totalAssigned > 0
          ? Number(((totalAchieved / totalAssigned) * 100).toFixed(2))
          : 0;
        const totalDaily = group.children.reduce((s, c) => s + c.dailyCount, 0);
        const maxDate = group.children.reduce((m, c) => {
          if (!c.lastDate) return m;
          if (!m || String(c.lastDate) > String(m)) return c.lastDate;
          return m;
        }, null);
        const distinctCurrencies = Array.from(
          new Set(group.children.map(c => c.currency).filter(Boolean))
        ).join(', ');
        drilldownRows.push([
          group.name,
          group.email,
          'ALL',
          'ALL',
          '',
          distinctCurrencies,
          '', // native sum not meaningful across currencies
          '',
          Math.round(totalAssigned * 100) / 100,
          Math.round(totalAchieved * 100) / 100,
          Math.round(totalPending * 100) / 100,
          totalPerf,
          maxDate ? moment(maxDate).format('DD-MM-YYYY') : '',
          totalDaily,
        ]);
        // Indented child rows — one per (period, target) the assignee is in.
        group.children.forEach(c => {
          const pending = c.assigned - c.achieved;
          const perf = c.assigned > 0
            ? Number(((c.achieved / c.assigned) * 100).toFixed(2))
            : 0;
          drilldownRows.push([
            '    ↳ ' + c.targetTitle,
            '',
            c.period,
            c.targetTitle,
            c.window,
            c.currency,
            Math.round(c.assignedNative * 100) / 100,
            Math.round(c.achievedNative * 100) / 100,
            Math.round(c.assigned * 100) / 100,
            Math.round(c.achieved * 100) / 100,
            Math.round(pending * 100) / 100,
            perf,
            c.lastDate ? moment(c.lastDate).format('DD-MM-YYYY') : '',
            c.dailyCount,
          ]);
        });
      });
      const wsDrilldown = XLSX.utils.aoa_to_sheet([...meta, drilldownHeaders, ...drilldownRows]);
      XLSX.utils.book_append_sheet(wb, wsDrilldown, 'Assignee Drilldown');

      // Sheet 5: Daily achievements detail (one row per day per assignee per target)
      const dailyHeaders = [
        'Period', 'Target Name', 'Assignee', 'Achievement Date',
        'RNC (native)', 'REC (native)', 'Number Ach',
        `Total (${baseCurrency})`,
      ];
      const dailyRows = [];
      periodReportData.forEach(item => {
        (item.targets || []).forEach(t => {
          (t.assignees || []).forEach(a => {
            (a.daily || []).forEach(d => {
              dailyRows.push([
                item.period, t.title, a.name,
                d.ach_date ? moment(d.ach_date).format('YYYY-MM-DD') : '',
                d.rnc, d.rec, d.number, d.total_base,
              ]);
            });
          });
        });
      });
      const wsDaily = XLSX.utils.aoa_to_sheet([...meta, dailyHeaders, ...dailyRows]);
      XLSX.utils.book_append_sheet(wb, wsDaily, 'Daily Detail');

      XLSX.writeFile(wb, `Sales_Report_${reportPeriod}_${ts}.xlsx`);
      return; // bypass the generic single-sheet flow below
    }

    if (false && showPeriodReport && periodReportData.length > 0) {
      // legacy single-sheet path (unused; kept for safety while we refactor)
    } else if (!showPeriodReport && filteredPerformanceData.length > 0) {
      metadata = buildFilterMetadata('dashboard');
      const headers = [
        'Target Title', 'Assignee', 'Period',
        'Start Date', 'End Date', 'Currency (native)',
        `Assigned (${baseCurrency})`, 'Assigned (native)',
        `Achievement (${baseCurrency})`, 'Achievement (native)',
        'Achievement %', `Remaining (${baseCurrency})`,
        'Last Achievement Date',
      ];
      dataRows = [headers, ...filteredPerformanceData.map(item => [
        item.title,
        item.assigneeName,
        item.period,
        item.startDate ? moment(item.startDate).format('YYYY-MM-DD') : '',
        item.endDate ? moment(item.endDate).format('YYYY-MM-DD') : '',
        item.currency || baseCurrency,
        item.targetValue,
        item.targetValueNative != null ? item.targetValueNative : item.targetValue,
        item.achievement,
        item.achievementNative != null ? item.achievementNative : item.achievement,
        item.achievementPercentage,
        item.remaining,
        item.lastAchievementDate ? moment(item.lastAchievementDate).format('YYYY-MM-DD') : '',
      ])];
      sheetName = 'Dashboard';
      fileName = `Sales_Dashboard_${ts}.xlsx`;
    } else {
      return;
    }
    const aoa = [...metadata, ...dataRows];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, fileName);
  };

  // Chart: stacked bars per target with one segment per assignee
  const getChartData = () => {
    // Group filteredPerformanceData by target title; build a dataset per assignee.
    const titles = Array.from(new Set(filteredPerformanceData.map(p => p.title)));
    const assigneeNames = Array.from(new Set(filteredPerformanceData.map(p => p.assigneeName)));
    const palette = [
      'rgba(54, 162, 235, 0.7)', 'rgba(75, 192, 192, 0.7)',
      'rgba(255, 159, 64, 0.7)', 'rgba(153, 102, 255, 0.7)',
      'rgba(255, 99, 132, 0.7)', 'rgba(255, 205, 86, 0.7)',
      'rgba(201, 203, 207, 0.7)',
    ];
    const targetDatasets = assigneeNames.map((name, i) => ({
      label: `${name} - Target (${targetType === 'amount' ? baseCurrency : '#'})`,
      data: titles.map(t => {
        const row = filteredPerformanceData.find(p => p.title === t && p.assigneeName === name);
        return row ? row.targetValue : 0;
      }),
      backgroundColor: palette[i % palette.length],
      borderWidth: 1,
      stack: 'target',
    }));
    const achievementDatasets = assigneeNames.map((name, i) => ({
      label: `${name} - Achievement (${targetType === 'amount' ? baseCurrency : '#'})`,
      data: titles.map(t => {
        const row = filteredPerformanceData.find(p => p.title === t && p.assigneeName === name);
        return row ? row.achievement : 0;
      }),
      backgroundColor: palette[(i + 3) % palette.length],
      borderWidth: 1,
      stack: 'achievement',
    }));
    return { labels: titles, datasets: [...targetDatasets, ...achievementDatasets] };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      title: { display: true, text: `Sales Performance — ${targetType === 'amount' ? 'Amount (' + baseCurrency + ')' : 'Number'} Targets` },
      tooltip: {
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || '';
            if (label) label += ': ';
            if (targetType === 'amount') {
              label += new Intl.NumberFormat('en-IN').format(context.parsed.y) + ' ' + baseCurrency;
            } else {
              label += context.parsed.y;
            }
            return label;
          }
        }
      }
    },
    scales: {
      x: { stacked: true },
      y: {
        stacked: true,
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            if (targetType === 'amount') {
              return new Intl.NumberFormat('en-IN', { notation: 'compact', compactDisplay: 'short' }).format(value);
            }
            return value;
          }
        }
      }
    }
  };

  const DateCell = (props) => {
    const date = props.dataItem[props.field];
    return <td>{date ? moment(date).format('DD-MM-YYYY') : '-'}</td>;
  };
  const ValueCell = (props) => <td className="text-end">{formatValue(props.dataItem[props.field])}</td>;
  const PercentageCell = (props) => {
    const percentage = Number(props.dataItem.achievementPercentage) || 0;
    const color = percentage >= 75 ? 'text-success' : percentage >= 50 ? 'text-warning' : 'text-danger';
    return <td className={`text-end ${color} fw-bold`}>{percentage}%</td>;
  };
  const AssigneeCell = (props) => {
    const { dataItem } = props;
    return (
      <td>
        <div className="fw-bold">{dataItem.assigneeName || '-'}</div>
        {dataItem.assigneeEmail && <div className="small text-muted">{dataItem.assigneeEmail}</div>}
      </td>
    );
  };
  const LastAchCell = (props) => {
    const d = props.dataItem.lastAchievementDate;
    return <td>{d ? moment(d).format('DD-MM-YYYY') : <span className="text-muted">—</span>}</td>;
  };

  const resetFilters = () => {
    setFromDate(''); setToDate(''); setSelectedAssignee('');
    setLastAchFrom(''); setLastAchTo('');
    setTargetType('amount'); setReportPeriod('monthly'); setShowPeriodReport(false);
  };

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="mb-0">
          <i className="fas fa-chart-line me-2"></i>
          Sales Report
          <span className="badge bg-secondary ms-2" title="Reports aggregate across currencies using your company's base currency">
            Base: {baseCurrency}
          </span>
        </h3>
      </div>

      <div className="card mb-4 dashboard-filter">
        <div className="card-body p-4">
          <div className="row g-3">
            <div className="col-md-2">
              <label className="form-label fw-bold small d-block mb-2"><i className="fas fa-bullseye me-1"></i>Target Type</label>
              <select className="form-select form-select-sm filter-input" value={targetType} onChange={(e) => setTargetType(e.target.value)} disabled={loading}>
                <option value="amount">Target Amount</option>
                <option value="number">Target Number</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label fw-bold small d-block mb-2" title="Targets whose window overlaps this date are included">
                <i className="fas fa-calendar me-1"></i>Target Window From
              </label>
              <input type="date" className="form-control form-control-sm filter-input" value={fromDate} onChange={(e) => setFromDate(e.target.value)} disabled={loading} />
            </div>
            <div className="col-md-2">
              <label className="form-label fw-bold small d-block mb-2" title="Targets whose window overlaps this date are included">
                <i className="fas fa-calendar me-1"></i>Target Window To
              </label>
              <input type="date" className="form-control form-control-sm filter-input" value={toDate} onChange={(e) => setToDate(e.target.value)} disabled={loading} />
            </div>
            <div className="col-md-2">
              <label className="form-label fw-bold small d-block mb-2" title="Filter the Target Summary grid by the date of each assignee's last saved achievement">
                <i className="fas fa-history me-1"></i>Last Ach From
              </label>
              <input type="date" className="form-control form-control-sm filter-input" value={lastAchFrom} onChange={(e) => setLastAchFrom(e.target.value)} disabled={loading || showPeriodReport} />
            </div>
            <div className="col-md-2">
              <label className="form-label fw-bold small d-block mb-2" title="Filter the Target Summary grid by the date of each assignee's last saved achievement">
                <i className="fas fa-history me-1"></i>Last Ach To
              </label>
              <input type="date" className="form-control form-control-sm filter-input" value={lastAchTo} onChange={(e) => setLastAchTo(e.target.value)} disabled={loading || showPeriodReport} />
            </div>
            <div className="col-md-2">
              <label className="form-label fw-bold small d-block mb-2"><i className="fas fa-user me-1"></i>Assignee</label>
              <select className="form-select form-select-sm filter-input" value={selectedAssignee} onChange={(e) => setSelectedAssignee(e.target.value)} disabled={loading}>
                <option value="">All Assignees</option>
                {assigneesList.map(a => (<option key={a.id} value={a.id}>{a.name}</option>))}
              </select>
            </div>
            <div className="col-md-4 col-lg-3">
              <label className="form-label fw-bold small d-block mb-2"><i className="fas fa-chart-pie me-1"></i>Report Type</label>
              <div className="btn-group w-100 filter-report-type" role="group" style={{ flexWrap: 'nowrap' }}>
                <input type="radio" className="btn-check" name="reportType" id="dashboardBtn" checked={!showPeriodReport} onChange={() => setShowPeriodReport(false)} disabled={loading} />
                <label
                  className="btn btn-outline-primary btn-sm text-nowrap"
                  htmlFor="dashboardBtn"
                  title="Per-target summary with chart"
                  style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  <i className="fas fa-tachometer-alt me-1"></i>Summary
                </label>
                <input type="radio" className="btn-check" name="reportType" id="reportBtn" checked={showPeriodReport} onChange={() => setShowPeriodReport(true)} disabled={loading} />
                <label
                  className="btn btn-outline-primary btn-sm text-nowrap"
                  htmlFor="reportBtn"
                  title="Totals grouped by week / month / quarter / year"
                  style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  <i className="fas fa-table me-1"></i>Roll-up
                </label>
              </div>
            </div>
            <div className="col-auto d-flex align-items-end">
              <button
                className="btn btn-outline-secondary btn-sm filter-reset-btn text-nowrap"
                onClick={resetFilters}
                disabled={loading}
                style={{ whiteSpace: 'nowrap' }}
              >
                <i className="fas fa-redo-alt me-1"></i>Reset
              </button>
            </div>
            {showPeriodReport && (
              <div className="col-md-2">
                <label className="form-label fw-bold small d-block mb-2"><i className="fas fa-calendar-alt me-1"></i>Period</label>
                <select className="form-select form-select-sm" value={reportPeriod} onChange={(e) => setReportPeriod(e.target.value)} disabled={loading}>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
            )}
          </div>

          <div className="alert alert-light border mt-3 py-2 small mb-0">
            <i className="fas fa-info-circle me-2 text-primary"></i>
            <strong>Target Summary</strong>: one row per assignee per target, with last achievement date. &nbsp;
            <strong>Period Roll-up</strong>: totals grouped by week / month / quarter / year.
            &nbsp;&middot;&nbsp; Date filters match targets whose <em>window overlaps</em> the chosen range.
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <i className="fas fa-exclamation-triangle me-2"></i>{error}
          <button type="button" className="btn-close" onClick={() => setError(null)}></button>
        </div>
      )}

      {emptyMessage && !error && !loading && (
        <div className="alert alert-info">
          <i className="fas fa-info-circle me-2"></i>{emptyMessage}
        </div>
      )}

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : showPeriodReport ? (
        <div className="card">
          <div className="card-header bg-light d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <i className="fas fa-table me-2"></i>
              {reportPeriod.charAt(0).toUpperCase() + reportPeriod.slice(1)} Sales Report
            </h5>
            <button className="btn btn-success btn-sm" onClick={exportToExcel} disabled={periodReportData.length === 0}>
              <i className="fas fa-download me-2"></i>Download Excel
            </button>
          </div>
          <div className="card-body">
            <Grid
              data={process(periodReportData, dataState)}
              pageable={{ buttonCount: 5, info: true, pageSizes: [10, 20, 50] }}
              sortable
              onDataStateChange={handleDataStateChange}
              {...dataState}>
              <GridColumn field="period" title="Period" width="150px" />
              <GridColumn field="targetCount" title="# Targets" width="100px" />
              <GridColumn field="targetTitles" title="Target Names" cell={(p) => (
                <td title={p.dataItem.targetTitles}>{p.dataItem.targetTitles || ''}</td>
              )} width="260px" />
              <GridColumn field="totalTarget" title={`Total Target (${baseCurrency})`} cell={ValueCell} width="200px" />
              <GridColumn field="totalAchievement" title={`Total Achievement (${baseCurrency})`} cell={ValueCell} width="220px" />
              <GridColumn field="achievementPercentage" title="Achievement %" cell={PercentageCell} width="150px" />
              <GridColumn field="remaining" title={`Remaining (${baseCurrency})`} cell={ValueCell} width="180px" />
            </Grid>
          </div>
        </div>
      ) : (
        <>
          <div className="row g-3 mb-4">
            <div className="col-md-3">
              <div className="card border-primary">
                <div className="card-body">
                  <h6 className="card-subtitle mb-2 text-muted">Total Target</h6>
                  <h3 className="card-title text-primary mb-0">{formatValue(summary.totalTarget)}</h3>
                  {summary.assigneeRowsCount != null && (
                    <div className="small text-muted">across {summary.assigneeRowsCount} assignment(s)</div>
                  )}
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card border-success">
                <div className="card-body">
                  <h6 className="card-subtitle mb-2 text-muted">Total Achievement</h6>
                  <h3 className="card-title text-success mb-0">{formatValue(summary.totalAchievement)}</h3>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card border-warning">
                <div className="card-body">
                  <h6 className="card-subtitle mb-2 text-muted">Remaining</h6>
                  <h3 className="card-title text-warning mb-0">{formatValue(summary.remaining)}</h3>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card border-info">
                <div className="card-body">
                  <h6 className="card-subtitle mb-2 text-muted">Achievement %</h6>
                  <h3 className="card-title text-info mb-0">{summary.achievementPercentage || 0}%</h3>
                </div>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-header bg-light d-flex justify-content-between align-items-center">
              <h5 className="mb-0"><i className="fas fa-chart-bar me-2"></i>Performance Chart</h5>
              <button className="btn btn-success btn-sm" onClick={exportToExcel} disabled={filteredPerformanceData.length === 0}>
                <i className="fas fa-download me-2"></i>Download Excel
              </button>
            </div>
            <div className="card-body" style={{ height: '420px' }}>
              {filteredPerformanceData.length > 0 ? (
                <Chart type="bar" data={getChartData()} options={chartOptions} />
              ) : (
                <div className="text-center text-muted py-5">
                  <i className="fas fa-chart-bar fa-3x mb-3"></i>
                  <p>No data available for the selected filters</p>
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-header bg-light">
              <h5 className="mb-0"><i className="fas fa-table me-2"></i>Sales Data &amp; Performance (per assignee)</h5>
            </div>
            <div className="card-body">
              <Grid
                data={process(filteredPerformanceData, dataState)}
                pageable={{ buttonCount: 5, info: true, pageSizes: [10, 25, 50, 100] }}
                sortable
                onDataStateChange={handleDataStateChange}
                {...dataState}>
                <GridColumn field="title" title="Target Title" width="180px" />
                <GridColumn field="assigneeName" title="Assignee" cell={AssigneeCell} width="200px" />
                <GridColumn field="period" title="Period" width="100px" />
                <GridColumn field="startDate" title="Start Date" cell={DateCell} width="120px" />
                <GridColumn field="endDate" title="End Date" cell={DateCell} width="120px" />
                <GridColumn field="targetValue" title={targetType === 'amount' ? `Assigned (${baseCurrency})` : 'Assigned #'} cell={ValueCell} width="170px" />
                <GridColumn field="achievement" title={targetType === 'amount' ? `Achievement (${baseCurrency})` : 'Achievement #'} cell={ValueCell} width="190px" />
                <GridColumn field="achievementPercentage" title="Achievement %" cell={PercentageCell} width="140px" />
                <GridColumn field="remaining" title={targetType === 'amount' ? `Remaining (${baseCurrency})` : 'Remaining'} cell={ValueCell} width="170px" />
                <GridColumn field="lastAchievementDate" title="Last Achievement Date" cell={LastAchCell} width="180px" />
              </Grid>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
